import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LoadingSpinner from 'component/LoadingSpinner';
import PpobService from 'service/PPOB/PpobService';
import { PpobGroupItem, PpobItem } from '../../domain/payload/response/PPOB/PpobResp'
import { hasPpobAccess } from 'helpers/hasPrivilege';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

const PpobDetail: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const ppobService = PpobService();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [group, setGroup] = useState<PpobGroupItem | null>(null);
  const [items, setItems] = useState<PpobItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isTogglingGroup, setIsTogglingGroup] = useState(false);
  const [isTogglingItem, setIsTogglingItem] = useState<string | null>(null);

  const canUpdate = hasPpobAccess(profile, 'update');

  const fetchGroupDetail = async () => {
    const token = profile?.data.token;
    if (!token || !id) {
      if (!token) window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await ppobService.getGroupDetail(token, id);
      setGroup(res.data!);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail group';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchItems = async () => {
    const token = profile?.data.token;
    if (!token || !id) return;

    setIsLoadingItems(true);
    try {
      const res = await ppobService.getItemList(token, id, searchKeyword, currentPage, pageSize);
      const sortedData = (res.data || []).sort((a, b) => (a.sort || 0) - (b.sort || 0));
      setItems(sortedData);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data item';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingItems(false);
    }
  };

  useEffect(() => {
    fetchGroupDetail();
  }, [id]);

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchKeyword]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSearchKeyword(searchTerm);
    setCurrentPage(1);
  };

  const handleToggleGroupActive = async () => {
    if (!group || !canUpdate) return;

    const token = profile?.data.token;
    if (!token) return;

    setIsTogglingGroup(true);
    try {
      await ppobService.toggleGroupActive(token, {
        id: group.id,
        isActive: !group.isActive
      });
      setGroup({ ...group, isActive: !group.isActive });
    } catch (err: any) {
      console.error(err);
      window.alert('Gagal mengubah status group');
    } finally {
      setIsTogglingGroup(false);
    }
  };

  const handleToggleItemActive = async (item: PpobItem) => {
    if (!canUpdate) return;

    const token = profile?.data.token;
    if (!token) return;

    setIsTogglingItem(item.id);
    try {
      await ppobService.toggleItemActive(token, {
        id: item.id,
        isActive: !item.isActive
      });
      setItems(items.map(i =>
        i.id === item.id ? { ...i, isActive: !i.isActive } : i
      ));
    } catch (err: any) {
      console.error(err);
      window.alert('Gagal mengubah status item');
    } finally {
      setIsTogglingItem(null);
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setIsDragging(false);

    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (dragIndex === dropIndex) return;

    const newItems = [...items];
    const draggedItem = newItems[dragIndex];
    newItems.splice(dragIndex, 1);
    newItems.splice(dropIndex, 0, draggedItem);

    const updatedItems = newItems.map((item, idx) => ({
      id: item.id,
      sort: idx + 1
    }));

    setItems(newItems);

    const token = profile?.data.token;
    if (token) {
      try {
        await ppobService.updateItemSort(token, { items: updatedItems });
        await fetchItems();
      } catch (err: any) {
        console.error('Failed to update sort:', err);
        window.alert('Gagal menyimpan urutan item. Silakan coba lagi.');
        await fetchItems();
      }
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive
      ? <span className="badge bg-success">Active</span>
      : <span className="badge bg-secondary">Inactive</span>;
  };

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4 fw-bold">PPOB Group Detail</h4>
        <div className="p-4 bg-white mb-4 rounded shadow-sm">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                  <h5 className="fw-bold mb-1">{group?.name}</h5>
                  <p className="text-muted mb-1">
                    Code: {group?.code}
                  </p>
                  <p className="fw-semibold text-secondary mb-0">
                    Status: {getStatusBadge(group?.isActive || false)}
                  </p>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {canUpdate && (
                    <div className="form-check form-switch" style={{ transform: 'scale(1.8)', transformOrigin: 'right center' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="groupActiveSwitch"
                        checked={group?.isActive || false}
                        onChange={handleToggleGroupActive}
                        disabled={isTogglingGroup}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="border-top pt-3" />

              <div className="d-flex justify-content-between align-items-center mb-3 py-2">
                <form className="d-flex align-items-center" onSubmit={handleSearchClick}>
                  <input
                    type="text"
                    className="form-control me-2"
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: '300px' }}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary d-flex align-items-center justify-content-center"
                  >
                    <i className="fa-solid fa-search"></i>
                  </button>
                </form>
              </div>

              <div className="table-responsive">
                <table className="table table-condensed table-hover">
                  <thead className="table-primary">
                    <tr>
                      <th style={{ width: '60px' }}>Sort</th>
                      <th>ID</th>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Icon</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isLoadingItems ? (
                      items.length > 0 ? (
                        items.map((item, index) => (
                          <tr
                            key={item.id}
                            draggable={canUpdate}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index)}
                            style={{
                              cursor: canUpdate ? 'grab' : 'default',
                              opacity: isDragging ? 0.5 : 1,
                              backgroundColor: isDragging ? '#f8f9fa' : 'transparent'
                            }}
                          >
                            <td style={{ cursor: 'grab' }}>
                              <i className="fa-solid fa-grip-vertical text-muted me-2"></i>
                              {item.sort || index + 1}
                            </td>
                            <td>{item.id}</td>
                            <td>{item.code}</td>
                            <td>{item.name}</td>
                            <td>
                              {item.iconUrl && (
                                <img
                                  src={item.iconUrl}
                                  alt={item.name}
                                  style={{ width: 30, height: 30, objectFit: 'contain' }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                            </td>
                            <td>
                              {getStatusBadge(item.isActive)}
                            </td>
                            <td>{item.createdAt}</td>
                            <td>
                              <div className="form-check form-switch d-inline-block">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id={`itemActiveSwitch-${item.id}`}
                                  checked={item.isActive}
                                  onChange={() => handleToggleItemActive(item)}
                                  disabled={isTogglingItem === item.id}
                                  style={{ transform: 'scale(1.1)', cursor: canUpdate ? 'pointer' : 'default' }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-3">
                            Tidak ada data item
                          </td>
                        </tr>
                      )
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalRecords > 0 && (
                <div className="d-flex justify-content-end align-items-center mt-3">
                  <div className="d-flex align-items-center">
                    <div className="me-4 d-flex align-items-center">
                      <label className="me-2">Rows per page:</label>
                      <CustomDropdown
                        value={pageSize}
                        options={PageSizeOption.options}
                        onChange={(opt) => {
                          setPageSize(Number(opt.id));
                          setCurrentPage(1);
                        }}
                        align="top"
                      />
                    </div>

                    <span className="me-3">
                      {totalRecords > 0
                        ? `${(currentPage - 1) * pageSize + 1}–${Math.min(
                          currentPage * pageSize,
                          totalRecords
                        )} of ${totalRecords}`
                        : "0 data"}
                    </span>

                    <nav>
                      <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(1)}
                            style={{ border: 0 }}
                          >
                            <i className="fa fa-angle-double-left"></i>
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            style={{ border: 0 }}
                          >
                            <i className="fa fa-angle-left"></i>
                          </button>
                        </li>
                        <li className="page-item disabled">
                          <span className="page-link" style={{ border: 0 }}>
                            {currentPage}
                          </span>
                        </li>
                        <li
                          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                            }
                            style={{ border: 0 }}
                          >
                            <i className="fa fa-angle-right"></i>
                          </button>
                        </li>
                        <li
                          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(totalPages)}
                            style={{ border: 0 }}
                          >
                            <i className="fa fa-angle-double-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PpobDetail;